import { useState, useEffect } from 'react';
import { Smartphone, Download, CheckCircle2, Share } from 'lucide-react';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

/**
 * Compact "Install App" (PWA) control for the top bar.
 *
 * Renders a small phone icon button — RED when the app is NOT installed,
 * GREEN when it is (standalone/installed). Clicking it opens a popover with
 * the full install walkthrough. Takes zero space in the main dashboard body.
 */
export const InstallAppButton = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);

  const isStandalone =
    (typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(display-mode: standalone)').matches) ||
    (typeof navigator !== 'undefined' && navigator.standalone === true);

  const isIOS =
    typeof navigator !== 'undefined' &&
    /iphone|ipad|ipod/i.test(navigator.userAgent) &&
    !/crios|fxios/i.test(navigator.userAgent);

  useEffect(() => {
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') setInstalled(true);
    setDeferredPrompt(null);
  };

  const alreadyInstalled = installed || isStandalone;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          title={alreadyInstalled ? 'App installed' : 'Install the app'}
          data-testid="apk-install-button"
        >
          <Smartphone className={`h-4 w-4 ${alreadyInstalled ? 'text-emerald-600' : 'text-red-500'}`} />
          <span
            className={`absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-background ${
              alreadyInstalled ? 'bg-emerald-500' : 'bg-red-500'
            }`}
            data-testid="apk-status-dot"
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80" data-testid="apk-install-popover">
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-teal-700 flex items-center justify-center shrink-0">
              <img src="/icon-192.png" alt="App icon" className="h-8 w-8 rounded-lg" data-testid="apk-app-icon" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold leading-tight">Passenger Amenities Health Monitor</p>
              <p className={`text-xs font-medium mt-0.5 ${alreadyInstalled ? 'text-emerald-600' : 'text-red-500'}`}>
                {alreadyInstalled ? 'Installed on this device' : 'Not installed'}
              </p>
            </div>
          </div>

          {alreadyInstalled ? (
            <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2" data-testid="apk-installed-badge">
              <CheckCircle2 className="h-4 w-4 shrink-0" />
              The app is installed — open it from your home screen or desktop.
            </div>
          ) : deferredPrompt ? (
            <Button onClick={handleInstall} className="w-full bg-teal-700 hover:bg-teal-800" data-testid="apk-one-tap-install">
              <Download className="h-4 w-4 mr-2" />
              Install App
            </Button>
          ) : isIOS ? (
            <div className="text-xs text-slate-700 bg-slate-50 border rounded-lg px-3 py-2 flex items-start gap-2" data-testid="apk-ios-instructions">
              <Share className="h-4 w-4 mt-0.5 text-teal-700 shrink-0" />
              <span>On iPhone/iPad: tap <strong>Share</strong> in Safari → <strong>Add to Home Screen</strong>.</span>
            </div>
          ) : (
            <div className="text-xs text-slate-700 bg-slate-50 border rounded-lg px-3 py-2 space-y-1.5" data-testid="apk-manual-instructions">
              <p className="font-medium text-slate-800">To install:</p>
              <p><strong>Desktop (Chrome/Edge):</strong> click the install icon in the address bar, or menu → <strong>Install app</strong>.</p>
              <p><strong>Android:</strong> Chrome menu → <strong>Add to Home screen</strong> / <strong>Install app</strong>.</p>
              <p className="text-[11px] text-muted-foreground pt-0.5">The one-tap button appears automatically once the browser offers it.</p>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default InstallAppButton;
